import Spinner from "components/Spinner";
import WOFileManager from "components/WorkOrderOrderEmail/WOFileManager";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function WoExcelApproval({
  selectedWorkOrder,
  refreshFunc,
  approvalData,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [excelFile, setExcelFile] = useState(null);

  // const [sendNameApproval, setSendNameApproval] = useState(true);

  const [existingExcelFile, setExistingExcelFile] = useState(false);
  const [excelFileError, setExcelFileError] = useState(null);
  const [excelApprovalStatus, setExcelApprovalStatus] =
    useState("pending_approval");

  const [showFilesModal, setShowFilesModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [excelFileType] = useState("file");

  /*   All Functions
   ********************************************* */

  const uploadExcelFile = async (e) => {
    e.preventDefault();
    // console.log("Uploading excel file:", excelFile);
    // console.log("sendNameApproval:", sendNameApproval);

    if (excelFileType === "file" && !excelFile) {
      return setExcelFileError("Please select an Excel file to upload.");
    }
    if (excelFileType === "file" && !validateExcelFile(excelFile)) {
      return;
    }
    if (excelFileType === "choose" && selectedFiles.length === 0) {
      return setExcelFileError("Please select an Excel file to upload.");
    }

    // return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("excelFileType", excelFileType);

      if (excelFileType === "file") {
        let fileName = `${Date.now()}_${excelFile.name}`;
        // update File name and path in form data
        let updatedFile = new File([excelFile], fileName, {
          type: excelFile.type,
        });
        formData.append("file", updatedFile);
      }
      if (excelFileType === "choose" && selectedFiles.length > 0) {
        let filePath = selectedFiles[0];
        formData.append("filePath", filePath);
      }

      // formData.append("sendNameApproval", sendNameApproval);
      formData.append("wId", selectedWorkOrder?.id);
      formData.append("WoId", selectedWorkOrder?.woId);

      const { data } = await API.post(
        "/wo-lot-approvals/upload-excel",
        formData,
        {
          headers: {
            ...authHeader(),
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (data.success) {
        await refreshFunc();
        dispatch(
          setNotification({
            message: "Image uploaded successfully.",
            type: "success",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const validateExcelFile = (file) => {
    // If Excel file is already approved or no file is selected (since it's optional), no need to validate
    if (approvalData?.excelFileStatus === "approved" || !file) {
      return true;
    }

    // If there's already an Excel file attached to the approval (but not approved), we don't need to validate
    if (existingExcelFile) {
      return true;
    }

    // Excel file is optional, so if no file is provided, that's fine
    if (!file) {
      return true;
    }

    const allowedExtensions = [".xlsx", ".xls"];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      setExcelFileError("Please select a valid Excel file (.xlsx or .xls)");
      return false;
    }

    setExcelFileError(null);
    return true;
  };

  const deleteExcelFile = async () => {
    if (!approvalData?.id) {
      return dispatch(
        setNotification({
          message: "Cannot delete Excel file, approval ID not found",
          type: "error",
        }),
      );
    }

    try {
      setLoading(true);
      const { data } = await API.delete(
        `/wo-lot-approval/excel/${approvalData.id}`,
        {
          headers: authHeader(),
        },
      );

      if (data?.success) {
        setExistingExcelFile(false);
        dispatch(
          setNotification({
            message: "Excel file deleted successfully",
            type: "success",
          }),
        );
        if (refreshFunc) {
          refreshFunc();
        }
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to delete Excel file",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message || "Error deleting Excel file",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "";
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (
      approvalData &&
      approvalData?.approvalExcelFile &&
      approvalData.approvalExcelFile !== "0"
    ) {
      setExistingExcelFile(true);
      // setSendNameApproval(approvalData.sendNameApproval || false);

      // Set excelApprovalStatus based on the data from server
      if (approvalData.excelFileStatus) {
        setExcelApprovalStatus(approvalData.excelFileStatus);
      }
    } else {
      setExistingExcelFile(false);
    }
  }, [approvalData]);

  return (
    <div>
      {loading && <Spinner />}
      <div className="mt-2">
        {/* Excel File Input - renamed to Player Names File */}
        {/* Upload only -- no "Choose File" from the WO's files (user,
            2026-10-27). That mode's code below is kept but unreachable. */}
        {excelFileType === "file" && (
          <form onSubmit={uploadExcelFile} className="mt-2">
            <label
              htmlFor="excelFile"
              className="form-label d-flex justify-content-between align-items-center"
            >
              <span className="fw-bold">Player Numbering Sheet (Optional)</span>
              {excelFile && !existingExcelFile && (
                <span className="badge bg-success">
                  File Selected: {excelFile.name}
                </span>
              )}
            </label>

            {existingExcelFile ? (
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1 border rounded p-2">
                    <div className="d-flex align-items-center">
                      <i
                        className="mdi mdi-file-excel text-success me-2"
                        style={{ fontSize: "24px" }}
                      ></i>
                      <span>Player Numbering Sheet is already attached</span>
                      {approvalData?.excelFileStatus === "approved" ? (
                        <>
                          <span
                            className={`badge mx-2 fs-5 px-2 bg-${getStatusColor(
                              approvalData?.excelFileStatus,
                            )}`}
                          >
                            {approvalData?.excelFileStatus
                              ?.replaceAll("_", " ")
                              ?.toUpperCase()}
                          </span>
                          <span
                            className={
                              "ms-auto badge bg-" +
                              getStatusColor(excelApprovalStatus)
                            }
                          >
                            ✓
                          </span>
                        </>
                      ) : (
                        <span
                          className={`badge mx-2 fs-5 px-2 bg-${getStatusColor(
                            approvalData?.excelFileStatus,
                          )}`}
                        >
                          {approvalData?.excelFileStatus
                            ?.replaceAll("_", " ")
                            ?.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger ms-2"
                    onClick={deleteExcelFile}
                    disabled={loading}
                  >
                    {loading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    ) : (
                      <i className="mdi mdi-delete"></i>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column">
                <input
                  type="file"
                  className={`form-control ${
                    excelFileError ? "is-invalid" : ""
                  }`}
                  id="excelFile"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setExcelFile(file);
                      validateExcelFile(file);
                    } else {
                      setExcelFile(null);
                    }
                  }}
                  required={!approvalData?.excelFileStatus}
                />
                {excelFileError && (
                  <div className="invalid-feedback">{excelFileError}</div>
                )}
                <div className="form-text text-muted">
                  Please select a single Excel file (.xlsx or .xls)
                </div>
              </div>
            )}

            {/* Show name approval option if file is selected */}
            {/* {(excelFile || existingExcelFile) && (
              <div className="mt-2 border rounded p-2 mt-3">
                <div className="form-label fw-bold">
                  <label>Send approval for player names & numbers?</label>
                </div>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="sendNameApproval"
                      id="sendNameApprovalYes"
                      checked={sendNameApproval === true}
                      onChange={(e) => {
                        setSendNameApproval(true);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="sendNameApprovalYes"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="sendNameApproval"
                      id="sendNameApprovalNo"
                      checked={sendNameApproval === false}
                      onChange={() => {
                        setSendNameApproval(false);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="sendNameApprovalNo"
                    >
                      No
                    </label>
                  </div>
                </div>
              </div>
            )} */}

            {!existingExcelFile && (
              <div className="text-end mt-2">
                <button
                  disabled={loading || !excelFile || excelFileError}
                  type="submit"
                  className="btn btn-primary"
                >
                  Upload
                </button>
              </div>
            )}
          </form>
        )}

        {excelFileType === "choose" && (
          <div className="mt-2">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Selected File</h4>
              <button
                className="btn btn-success btn-sm"
                onClick={() => setShowFilesModal(true)}
              >
                Browse
              </button>
            </div>
            <hr className="mt-1" />
            {selectedFiles.length > 0 ? (
              <ul className="list-group mt-2">
                {selectedFiles.map((file, index) => (
                  <li
                    key={index}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <h5 className="text-muted">{file?.split("/")?.pop()}</h5>
                    <button className="btn btn-danger btn-sm py-0 px-1 ">
                      <i
                        className="mdi mdi-close"
                        onClick={() => {
                          let updatedFiles = selectedFiles.filter(
                            (f) => f !== file,
                          );
                          setSelectedFiles(updatedFiles);
                        }}
                      ></i>
                    </button>
                  </li>
                ))}
                {/* {selectedFiles.length > 0 && (
                  <div className="mt-2 border rounded p-2 mt-3">
                    <div className="form-label fw-bold">
                      <label>Send approval for player names & numbers?</label>
                    </div>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="sendNameApproval"
                          id="sendNameApprovalYes"
                          checked={sendNameApproval === true}
                          onChange={(e) => {
                            setSendNameApproval(true);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="sendNameApprovalYes"
                        >
                          Yes
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="sendNameApproval"
                          id="sendNameApprovalNo"
                          checked={sendNameApproval === false}
                          onChange={() => {
                            setSendNameApproval(false);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="sendNameApprovalNo"
                        >
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                )} */}
              </ul>
            ) : (
              <div className="text-muted mt-2">No file selected</div>
            )}

            <div className="text-end mt-2">
              <button
                disabled={selectedFiles.length === 0 || loading}
                type="button"
                className="btn btn-primary"
                onClick={uploadExcelFile}
              >
                Upload
              </button>
            </div>
          </div>
        )}
      </div>
      {/*    Select Files
       ********************************************* */}
      <Modal
        size="lg"
        contentClassName="bg-transparent"
        show={showFilesModal}
        onHide={() => setShowFilesModal(false)}
      >
        <Modal.Body className="bg-white custom-shadow custom-border-radius">
          <h4 className="h3 mb-3">Select Files</h4>
          <WOFileManager
            selectedWO={selectedWorkOrder}
            setSelectedFiles={setSelectedFiles}
            setShowFilesModal={setShowFilesModal}
            multiple={false}
            fileExtensionFilter={["xlsx", "xls"]}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
